import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, UtensilsCrossed, Home as HomeIcon, ShieldCheck, Zap } from "lucide-react";

const C = {
  cream: "#FCF4EC",
  paper: "#FFFBF6",
  blush: "#EAC8C3",
  blushLine: "#E3B9B3",
  terracotta: "#B5654A",
  wine: "#7A3B2E",
  gold: "#C9A272",
  ink: "#3D2E28",
  inkSoft: "#8A6E63",
};
const display = "'Lora', Georgia, serif";
const body = "'Poppins', system-ui, sans-serif";

const Field = ({ label, value }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 9.5, letterSpacing: 1, textTransform: "uppercase", color: "#97655A", fontWeight: 600, fontFamily: body }}>
      {label}
    </div>
    <div style={{ borderBottom: `1.5px dotted ${C.terracotta}`, paddingBottom: 4, marginTop: 4, fontFamily: display, fontSize: 13, color: C.ink, minHeight: 18 }}>
      {value}
    </div>
  </div>
);

const Card = ({ children }) => (
  <div style={{ background: C.paper, border: "1px solid #E7CFC6", borderRadius: 10, padding: "16px 16px" }}>{children}</div>
);

const CatalogFrame = () => {
  const rows = [
    [Zap, "The 5-Minute Reset"],
    [BookOpen, "Working Mom Weekly Reset™"],
    [UtensilsCrossed, "The Meal Planner"],
    [HomeIcon, "Family Command Center"],
    [ShieldCheck, "The Boundaries Worksheet"],
  ];
  return (
    <div>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 9, letterSpacing: 2.5, color: C.terracotta, textTransform: "uppercase", fontFamily: body, fontWeight: 600 }}>
          The Thrive &amp; Mother Company™
        </div>
        <div style={{ fontFamily: display, fontWeight: 600, fontSize: 22, color: C.wine, marginTop: 6 }}>Your Tools</div>
      </div>
      {rows.map(([Icon, name]) => (
        <div key={name} style={{ background: C.paper, border: `1px solid ${C.blushLine}`, borderRadius: 10, padding: "11px 12px", marginBottom: 9, display: "flex", alignItems: "center", gap: 11 }}>
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.blush, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon size={15} color={C.wine} />
          </div>
          <div style={{ fontFamily: display, fontWeight: 600, fontSize: 13.5, color: C.wine }}>{name}</div>
        </div>
      ))}
    </div>
  );
};

const ResetFrame = () => (
  <div>
    <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 14 }}>
      {["R", "E", "S", "E", "T"].map((l, i) => (
        <div key={i} style={{ width: 24, height: 24, borderRadius: "50%", border: `1.4px solid ${C.terracotta}`, background: i === 0 ? C.terracotta : "transparent", color: i === 0 ? C.cream : C.wine, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: display, fontSize: 11 }}>
          {l}
        </div>
      ))}
    </div>
    <div style={{ fontSize: 9.5, letterSpacing: 2, color: C.gold, textTransform: "uppercase", fontFamily: body, marginBottom: 3 }}>Step One</div>
    <div style={{ fontFamily: display, fontWeight: 600, fontSize: 19, color: C.wine, marginBottom: 12 }}>Reflect</div>
    <Card>
      <Field label="What went well this week?" value="Protected two mornings for deep work." />
      <Field label="What am I most proud of?" value="I asked for help — and meant it." />
    </Card>
  </div>
);

const MealFrame = () => (
  <div>
    <div style={{ fontSize: 9.5, letterSpacing: 2, color: C.gold, textTransform: "uppercase", fontFamily: body, marginBottom: 3 }}>Monday – Friday</div>
    <div style={{ fontFamily: display, fontWeight: 600, fontSize: 19, color: C.wine, marginBottom: 12 }}>Weekday Meals</div>
    <Card>
      <Field label="Monday — Dinner" value="Sheet-pan salmon & greens" />
      <Field label="Tuesday — Dinner" value="Slow-cooker tacos" />
      <Field label="Wednesday — Dinner" value="Leftovers night" />
    </Card>
  </div>
);

const BoundariesFrame = () => (
  <div>
    <div style={{ fontSize: 9.5, letterSpacing: 2, color: C.gold, textTransform: "uppercase", fontFamily: body, marginBottom: 3 }}>Get Specific</div>
    <div style={{ fontFamily: display, fontWeight: 600, fontSize: 19, color: C.wine, marginBottom: 12 }}>Defining The Boundary</div>
    <Card>
      <Field label="What do I need instead?" value="No work email after 7pm." />
      <div style={{ fontSize: 9.5, letterSpacing: 1, textTransform: "uppercase", color: "#97655A", fontWeight: 600, fontFamily: body, marginBottom: 6 }}>How firm does this feel?</div>
      <div style={{ display: "flex", justifyContent: "center", gap: 9 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <div key={n} style={{ width: 26, height: 26, borderRadius: "50%", border: `1.4px solid ${C.terracotta}`, background: n === 4 ? C.terracotta : "transparent", color: n === 4 ? C.cream : C.wine, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: display, fontSize: 12 }}>
            {n}
          </div>
        ))}
      </div>
    </Card>
  </div>
);

const FRAMES = [
  { label: "Your toolkit", render: CatalogFrame },
  { label: "The RESET Method™", render: ResetFrame },
  { label: "Meal Planner", render: MealFrame },
  { label: "Boundaries Worksheet", render: BoundariesFrame },
];

export const AppWalkthrough = () => {
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % FRAMES.length), 3200);
    return () => clearInterval(t);
  }, []);

  const Frame = FRAMES[i].render;

  return (
    <div className="flex flex-col items-center" data-testid="app-walkthrough">
      {/* Fonts for the preview */}
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Lora:wght@500;600&family=Poppins:wght@400;500;600&display=swap"
      />
      {/* Phone mockup */}
      <div
        className="relative"
        style={{
          width: 300,
          borderRadius: 34,
          padding: 12,
          background: "#2A1F1B",
          boxShadow: "0 30px 60px -20px rgba(122,59,46,0.45)",
        }}
      >
        <div style={{ position: "absolute", top: 20, left: "50%", transform: "translateX(-50%)", width: 90, height: 6, borderRadius: 99, background: "#4a3a33", zIndex: 2 }} />
        <div
          style={{
            background: C.cream,
            borderRadius: 24,
            height: 520,
            overflow: "hidden",
            padding: "34px 18px 18px",
            position: "relative",
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={i}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
            >
              <Frame />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Caption + progress */}
      <p className="mt-6 text-sm text-charcoal/70 font-light" data-testid="walkthrough-caption">
        {FRAMES[i].label}
      </p>
      <div className="mt-3 flex items-center gap-2">
        {FRAMES.map((f, idx) => (
          <button
            key={f.label}
            onClick={() => setI(idx)}
            aria-label={`Show ${f.label}`}
            data-testid={`walkthrough-dot-${idx}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${idx === i ? "w-6 bg-terracotta" : "w-1.5 bg-terracotta/30"}`}
          />
        ))}
      </div>
    </div>
  );
};
